// docs/86 the chapter scene: between chapters of the World's storybook, a short scene shot like the
// intro (docs/21 §7) closes the chapter that ended and opens the next. Letterbox bars, a drifting
// painted scene per page and a cut through black between them. The pages: the chapter card (the
// chapter that ended, by the name the book remembers it by, turning over to the new chapter's
// numeral and title), the scene's panels with their spoken lines, the named foe's moment, and the
// new chapter's goal with the way back to the World. Click / Space / Enter finishes the line, then
// turns the page; ← goes back. Played once per browser; the Saga replays it.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button, HeroSprite, SightLayer, motionMs, useBeatScope } from '../../components';
import { useSession } from '../../../core/stores/session';
import { useRoute } from '../../../core/stores/route';
import { CLASS_NAMES } from '../../../core/sim/adapt';
import { classLook, heroToken } from '../../../game/heroKit';
import { useHeroLooks } from '../../../core/heroes/looks';
import { useBookChapters, useGenesis, useMyHeroes, useWorld, useWorldClock } from '../../../core/subscriptions/hooks';
import { display, useStoryMarks, withHeroes, type Genesis, type Scene, type StorySeason } from '../../../core/story/genesis';
import { chapterLabel, decodeChapterScene, numberWord, type ChapterScene } from '../../../core/story/chapter';
import { castPanel, storyContext, textSeed } from '../../../core/story/lexicon';
import { useDialogs } from '../../components/Modal';
import { SceneBackdrop } from './Scene';
import { SpokenLine, Typed, type IntroHero } from './Intro';
import './intro.css';
import { playSfx, preloadSfx } from '../../../core/audio';
import { TONE_MOOD, useMusicMood } from '../../../core/audio/music';
import { hotkeyFor } from '../../../core/input/hotkeys';

const PAGE_KEYS = ['story.next', 'story.back'] as const;

type Curtain = 'up' | 'falling' | 'down' | 'rising';
const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];
const SEASONS: readonly StorySeason[] = ['Spring', 'Summer', 'Autumn', 'Winter'];
const FALL_MS = 420, RISE_MS = 800, HOLD_MS = 900, FIRST_PAINT_MS = 2500;

/** The chapter scene over a decoded scene; the live screen and the `?chapter=1` harness both render it. */
export function ChapterView({ g, scene, number, heroes, season, onEnter, enterLabel = 'Onward', startAt = 0 }: {
  g: Genesis; scene: ChapterScene; number: number; heroes: IntroHero[]; season?: StorySeason;
  onEnter: () => void; enterLabel?: string; startAt?: number;
}) {
  const panels = scene.panels;
  const foe = scene.foe;
  // 0 the chapter card, 1..n panels, then the named foe (when there is one), then the new chapter's goal
  const reveal = foe ? panels.length + 1 : -1;
  const last = panels.length + (foe ? 2 : 1);
  const first = Math.min(startAt, last);
  const [step, setStep] = useState(first);
  const [aim, setAim] = useState(first);
  const aimRef = useRef(first);
  const [curtain, setCurtain] = useState<Curtain>('down');
  const curtainRef = useRef<Curtain>('down');
  curtainRef.current = curtain;
  const timer = useRef(0);
  const seen = useRef(new Set<number>());
  const sounded = useRef(new Set<number>());
  const stepRef = useRef(first);
  const [textDone, setTextDone] = useState(first === 0 || first === last);
  const [lineDone, setLineDone] = useState(false);
  const skipRef = useMemo(() => ({ current: () => {} }), []);
  const lineSkipRef = useMemo(() => ({ current: () => {} }), []);
  const names = heroes.map((h) => h.name);
  const panel = step >= 1 && step <= panels.length ? panels[step - 1] : undefined;
  const sceneArt: Scene = panel ? panel.scene : step === reveal ? 'Night' : step === 0 ? panels[0]?.scene ?? 'Fire' : panels[panels.length - 1]?.scene ?? 'Road';
  // docs/145: the heroes stand in the scene in their own kit (a token per look; a stand-in in its class's kit)
  const looks = useHeroLooks((s) => s.looks);
  const party = heroes.map((h) => heroToken(h.look ?? (h.id !== undefined ? looks[h.id.toString()] : undefined) ?? classLook(h.classId)));
  const partyKey = party.join(',');
  const casts = useMemo(() => {
    const ctx = storyContext(g, partyKey ? partyKey.split(',').map(Number) : []);
    return [
      castPanel({ heading: scene.closed_title, text: scene.closed_summary, line: null }, ctx),
      ...panels.map((p) => castPanel(p, ctx)),
      ...(foe ? [castPanel({ heading: foe.name, text: foe.text, line: { speaker: foe.name, text: foe.line } }, ctx)] : []),
    ];
  }, [g, scene, panels, foe, partyKey]);
  const cast = step < casts.length ? casts[step] : undefined;
  const castSeed = panel ? textSeed(panel.heading, panel.text) : textSeed(scene.title, String(step));

  const settle = (s: Curtain, ms: number, then?: () => void) => {
    window.clearTimeout(timer.current);
    setCurtain(s);
    if (then) timer.current = window.setTimeout(then, ms);
  };
  const lift = useCallback(() => {
    if (curtainRef.current !== 'down') return;
    curtainRef.current = 'rising';
    const at = stepRef.current;
    if (!sounded.current.has(at)) {
      sounded.current.add(at);
      playSfx(at === 0 ? 'cine.title' : at === reveal ? 'cine.reveal' : at === last ? 'cine.objective' : 'cine.chapter', { whenUnlocked: at === 0 });
    }
    settle('rising', motionMs(RISE_MS), () => setCurtain('up'));
  }, [reveal, last]);
  const commit = useCallback((to: number) => {
    const read = seen.current.has(to);
    playSfx('ui.page');
    playSfx('cine.page', { gain: 0.8 });
    skipRef.current = () => {}; lineSkipRef.current = () => {};
    setStep(to);
    stepRef.current = to;
    setTextDone(to === 0 || to === last || read);
    setLineDone(read);
    settle('down', HOLD_MS, () => lift());
  }, [last, skipRef, lineSkipRef, lift]);
  const onPainted = useCallback(() => {
    if (curtainRef.current !== 'down') return;
    requestAnimationFrame(() => requestAnimationFrame(lift));
  }, [lift]);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  useEffect(() => { const t = window.setTimeout(() => lift(), FIRST_PAINT_MS); return () => window.clearTimeout(t); }, [lift]);
  useEffect(() => { void preloadSfx(['cine.page', 'cine.title', 'cine.chapter', 'cine.line', 'cine.rise', 'cine.reveal', 'cine.objective'], true); }, []);
  const go = useCallback((to: number) => {
    const at = Math.max(0, Math.min(last, to));
    if (at === aimRef.current) return;
    if (at === reveal && at > aimRef.current && !sounded.current.has(at)) playSfx('cine.rise');
    aimRef.current = at;
    setAim(at);
    const c = curtainRef.current;
    if (c === 'falling') return;
    if (c === 'down' || !motionMs(FALL_MS)) { commit(at); return; }
    curtainRef.current = 'falling';
    settle('falling', motionMs(FALL_MS), () => commit(aimRef.current));
  }, [last, reveal, commit]);

  const onTyped = useCallback((skip: () => void, done: boolean) => { skipRef.current = skip; if (done) setTextDone(true); }, [skipRef]);
  const needsLine = !!panel?.line || step === reveal;
  const markLine = useCallback(() => setLineDone(true), []);
  const complete = textDone && (!needsLine || lineDone);
  const turning = aim !== step || curtain === 'falling' || curtain === 'down';
  useEffect(() => { if (complete) seen.current.add(step); }, [complete, step]);
  const next = useCallback(() => {
    if (!turning) {
      if (!textDone) { skipRef.current(); return; }
      if (needsLine && !lineDone) { lineSkipRef.current(); return; }
    }
    go(aimRef.current + 1);
  }, [turning, textDone, needsLine, lineDone, go, skipRef, lineSkipRef]);
  const back = useCallback(() => go(aimRef.current - 1), [go]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.tagName === 'BUTTON')) return;
      if (useDialogs.getState().queue.length || document.querySelector('.fk-modal')) return;
      const key = hotkeyFor(e, PAGE_KEYS);
      if (key === 'story.next') { e.preventDefault(); if (aimRef.current < last) next(); }
      else if (key === 'story.back') { e.preventDefault(); back(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [next, back, last]);
  const read = seen.current.has(step);
  useBeatScope(step);
  useMusicMood(step === reveal ? 'dread' : TONE_MOOD[g.realm.tone] ?? 'wonder', true);
  const pageText = panel ? withHeroes(panel.text, names) : step === reveal && foe ? withHeroes(foe.text, names) : '';
  const lineText = panel?.line ? withHeroes(panel.line.text, names) : step === reveal && foe ? withHeroes(foe.line, names) : '';

  const figures = step === last && heroes.length > 0
    ? heroes.slice(0, 6).map((h, i) => <span key={h.key} className="fk-intro__figure" style={{ ['--i' as string]: i - (Math.min(heroes.length, 6) - 1) / 2 }}><HeroSprite heroId={h.id} look={h.id === undefined ? h.look ?? classLook(h.classId) : undefined} frame={h.frame} classId={h.classId} scale={3} title={h.name} breathe /><span className="fk-intro__figure-name">{h.name}</span></span>)
    : undefined;
  const ended = number - 1;

  let body: ReactNode;
  if (step === 0) {
    body = (
      <div className="fk-intro__title fk-chapter__title" key="title" data-testid="chapter-title">
        <span className="fk-intro__eyebrow" data-testid="chapter-ended">The end of {chapterLabel(ended, scene.closed_title)}</span>
        <p className="fk-chapter__summary">{withHeroes(scene.closed_summary, names)}</p>
        <span className="fk-chapter__rule" aria-hidden="true">❦</span>
        <span className="fk-intro__eyebrow">{display(g.realm.name)}</span>
        <h1 className="fk-intro__realm" data-testid="chapter-number">Chapter {numberWord(number)}</h1>
        <p className="fk-intro__epithet" data-testid="chapter-name">{scene.title}</p>
      </div>
    );
  } else if (panel) {
    body = (
      <>
        <div className="fk-intro__page" key={`p${step}`}>
          <span className="fk-intro__chapter" data-testid="intro-chapter">{ROMAN[step - 1] ?? step}</span>
          <h2 className="fk-intro__heading">{panel.heading}</h2>
          <Typed className="fk-intro__text" text={pageText} onDone={onTyped} whole={read} />
        </div>
        {panel.line && textDone && (
          <SpokenLine key={`l${step}`} speaker={panel.line.speaker} text={lineText} onDone={markLine} skipRef={lineSkipRef} foe={!!foe && panel.line.speaker.trim().toLowerCase() === foe.name.trim().toLowerCase()} whole={read} />
        )}
      </>
    );
  } else if (step === reveal && foe) {
    body = (
      <>
        <div className="fk-intro__reveal" key="reveal" data-testid="chapter-foe">
          <span className="fk-intro__reveal-eyebrow">Meanwhile</span>
          <h2 className="fk-intro__reveal-name" data-testid="chapter-foe-name">{display(foe.name)}</h2>
          {foe.title && <span className="fk-intro__reveal-title">{foe.title}</span>}
          <Typed className="fk-intro__reveal-cry fk-chapter__foe-text" text={pageText} onDone={onTyped} whole={read} />
        </div>
        {textDone && <SpokenLine key={`f${step}`} speaker={display(foe.name)} text={lineText} onDone={markLine} skipRef={lineSkipRef} foe whole={read} />}
      </>
    );
  } else {
    body = (
      <div className="fk-intro__call" key="call">
        <div className="fk-panel fk-panel--ornate fk-intro__card" data-testid="chapter-goal">
          <span className="fk-intro__card-eyebrow">Chapter {numberWord(number)} begins</span>
          <h2 className="fk-intro__card-title">{scene.title}</h2>
          <p className="fk-intro__objective"><span>The chapter</span>{withHeroes(scene.goal, names)}</p>
          {foe && <div className="fk-intro__foe"><span className="fk-intro__foe-name">{display(foe.name)}</span><span className="fk-intro__foe-title">{foe.title}</span></div>}
          <div className="fk-intro__actions">
            <Button primary size="lg" onClick={onEnter} data-testid="chapter-enter">{enterLabel}</Button>
          </div>
          {heroes.length > 0 && <p className="fk-intro__party">{heroes.length === 1 ? `${names[0]} takes up the tale.` : `${names.slice(0, 6).join(', ')} take up the tale.`}</p>}
        </div>
      </div>
    );
  }

  return (
    <main className={`fk-intro fk-chapter ${step === reveal ? 'fk-intro--reveal' : ''}`} data-testid="chapter-screen" data-step={step} data-steps={last + 1} data-curtain={curtain}>
      <div className={`fk-intro__camera fk-intro__camera--${step % 2 ? 'a' : 'b'}`} key={`cam${step}`}>
        <SceneBackdrop scene={sceneArt} figures={figures} season={season ?? g.calendar?.season} biome={g.realm.biome} cast={cast} seed={castSeed} onPainted={onPainted} />
      </div>
      <SightLayer />
      <span className="fk-intro__letterbox fk-intro__letterbox--top" aria-hidden="true" />
      <span className="fk-intro__letterbox fk-intro__letterbox--bottom" aria-hidden="true" />
      <div className="fk-intro__stage fk-hit" data-testid="chapter-panel" data-scene={sceneArt} onClick={(e) => { if ((e.target as HTMLElement).closest('button, .fk-intro__card')) return; if (aimRef.current < last) next(); }}>
        {curtain !== 'down' && body}
      </div>
      <span className="fk-intro__curtain" data-state={curtain} aria-hidden="true" />
      <footer className="fk-intro__bar fk-hit">
        <Button small variant="ghost" disabled={aim === 0} onClick={back} data-testid="chapter-back">‹ Back</Button>
        <ol className="fk-intro__pips" aria-label={`Page ${aim + 1} of ${last + 1}`}>
          {Array.from({ length: last + 1 }, (_, i) => <li key={i} className={i === aim ? 'on' : i < aim ? 'done' : ''} />)}
        </ol>
        <span className="fk-intro__spacer" />
        {aim < last && <Button small variant="ghost" onClick={() => go(last)} data-testid="chapter-skip">Skip »</Button>}
        {aim < last && <Button small primary={complete || turning} onClick={next} data-testid="chapter-next">{complete || turning ? 'Next ›' : 'Finish line'}</Button>}
      </footer>
    </main>
  );
}

/** The newest chapter of my World whose scene this browser has not played, if any (docs/86). */
export function useUnseenChapter(worldId: bigint | undefined) {
  const world = useWorld(worldId);
  const chapters = useBookChapters(worldId);
  const marks = useStoryMarks();
  if (!world) return undefined;
  const latest = chapters[chapters.length - 1];
  return latest && latest.scene.length > 0 && !marks.chapterSeen(world, latest.number) ? latest : undefined;
}

/** The live chapter scene for the World I'm in (routed by RouteDriver; `chapter` replays one from the Saga). */
export function ChapterScreen() {
  const worldId = useSession((s) => s.worldId);
  const go = useRoute((s) => s.go);
  const replay = useRoute((s) => s.replay);
  const want = useRoute((s) => s.chapter);
  const g = useGenesis(worldId);
  const world = useWorld(worldId);
  const clock = useWorldClock(worldId);
  const chapters = useBookChapters(worldId);
  const marks = useStoryMarks();
  const heroes = useMyHeroes().filter((h) => h.worldId === worldId).sort((a, b) => (a.id < b.id ? -1 : 1));
  const row = (want !== undefined ? chapters.find((c) => c.number === want) : undefined) ?? [...chapters].reverse().find((c) => c.scene.length > 0);
  const scene = decodeChapterScene(row?.scene);
  const toWorld = () => {
    if (world && row) marks.setChapterSeen(world, row.number);
    go('world', { replay: undefined, chapter: undefined });
  };
  if (!worldId || !g || !row || !scene) return <main className="fk-intro" data-testid="chapter-screen" />;
  return (
    <ChapterView key={row.number} g={g} scene={scene} number={row.number} season={clock ? SEASONS[clock.season] : undefined}
      heroes={heroes.slice(0, 6).map((h) => ({ key: h.id.toString(), id: h.id, name: h.name, frame: h.oryxFrame, classId: CLASS_NAMES[h.class] ?? 'fighter' }))}
      enterLabel={replay ? 'Back to the World' : 'Onward'} onEnter={toWorld} />
  );
}
